import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { CombinedContext } from "../../../context/combinedContext";
import translations from "../../../translations/translations";
import ReactCountryFlag from "react-country-flag";
import { ArrowRight, CalendarCheck, Store, ShieldCheck, Smartphone, Layers, Sparkles, MapPin, Zap } from "lucide-react";
import useReveal from "../../common/useReveal";
import logo from "../../../images/logoTransparent.png";
import "./about.css";

/* Copy lives in translations; the icons are structural, so they live here. */
const ROADMAP_ICONS = [Smartphone, Layers, Sparkles];
const IDENTITY_ICONS = [MapPin, ShieldCheck, Zap];

const About = () => {
	const { language } = useContext(CombinedContext);
	const navigate = useNavigate();
	const rootRef = useReveal();

	const t = translations[language].about;

	const pillars = [
		{ icon: CalendarCheck, ...t.clients },
		{ icon: Store, ...t.staff },
		{ icon: ShieldCheck, ...t.tech },
	];

	return (
		<div className="sf-ab-root" ref={rootRef}>

			{/* ── HERO ── */}
			<section className="sf-ab-hero">
				<div className="sf-ab-hero-grid" aria-hidden="true"></div>

				<div className="sf-ab-hero-inner">
					<div className="sf-ab-hero-left">
						<span className="sf-ab-flag-pill">
							<ReactCountryFlag
								countryCode="DO"
								svg
								style={{ width: "1.15em", height: "1.15em" }}
								aria-label="Dominican Republic"
							/>
							{t.hero.flag}
						</span>

						<h1>{t.title}</h1>
						<p className="sf-ab-hero-tagline">{t.hero.tagline}</p>
						<p className="sf-ab-hero-intro">{t.hero.intro}</p>

						<div className="sf-ab-hero-actions">
							<button className="sf-ab-btn-primary" onClick={() => navigate("/businesslanding")}>
								{t.hero.primaryCta}
								<ArrowRight size={17} className="sf-ab-btn-arrow" />
							</button>
							<button className="sf-ab-btn-ghost" onClick={() => navigate("/barbershop")}>
								{t.hero.secondaryCta}
							</button>
						</div>
					</div>

					{/* Identity panel — restates, at a glance, what the copy says at length */}
					<aside className="sf-ab-hero-card">
						<div className="sf-ab-hero-card-glow" aria-hidden="true"></div>
						<div className="sf-ab-hero-card-inner">
							<img src={logo} alt="Sin Fila" className="sf-ab-hero-card-logo" />

							<ul className="sf-ab-identity">
								{t.hero.identity.map((row, i) => {
									const Icon = IDENTITY_ICONS[i] || MapPin;
									return (
										<li key={row.label}>
											<span className="sf-ab-identity-icon">
												<Icon size={16} strokeWidth={1.9} />
											</span>
											<span className="sf-ab-identity-text">
												<small>{row.label}</small>
												<strong>{row.value}</strong>
											</span>
										</li>
									);
								})}
							</ul>
						</div>
					</aside>
				</div>
			</section>

			{/* ── PAGE BODY ── */}
			<div className="sf-ab-page">

				{/* MISSION */}
				<section className="sf-ab-mission sf-ab-cream-bg">
					<div className="sf-ab-mission-inner" data-reveal>
						<span className="sf-ab-eyebrow">{t.eyebrows.mission}</span>
						<h2>{t.whoWeAre.title}</h2>
						<p>{t.whoWeAre.text}</p>
					</div>
				</section>

				{/* FOUNDER */}
				<section className="sf-ab-founder">
					<div className="sf-ab-founder-card" data-reveal>
						<span className="sf-ab-founder-mark" aria-hidden="true">&ldquo;</span>
						<span className="sf-ab-eyebrow">{t.eyebrows.founder}</span>
						<p className="sf-ab-founder-quote">{t.hero.founder}</p>
						<p className="sf-ab-founder-meta">{t.hero.founderMeta}</p>
					</div>
				</section>

				{/* PILLARS */}
				<section className="sf-ab-pillars">
					<div className="sf-ab-head-center" data-reveal>
						<span className="sf-ab-eyebrow">{t.eyebrows.pillars}</span>
						<h2>{t.pillars.title}</h2>
						<p className="sf-ab-head-sub">{t.pillars.subtitle}</p>
					</div>

					<div className="sf-ab-pillar-grid">
						{pillars.map(({ icon: Icon, label, title, text }) => (
							<article className="sf-ab-pillar-card" key={label} data-reveal>
								<div className="sf-ab-pillar-icon">
									<Icon size={22} strokeWidth={1.9} />
								</div>
								<span className="sf-ab-pillar-label">{label}</span>
								<h3>{title}</h3>
								<p>{text}</p>
							</article>
						))}
					</div>
				</section>

				{/* VISION + ROADMAP */}
				<section className="sf-ab-vision sf-ab-dyk-bg">
					<div className="sf-ab-vision-inner">
						<div className="sf-ab-vision-copy" data-reveal>
							<span className="sf-ab-eyebrow">{t.eyebrows.vision}</span>
							<h2>{t.future.title}</h2>
							<p>{t.future.text}</p>
							<p className="sf-ab-vision-lead">{t.hero.vision}</p>
						</div>

						<ul className="sf-ab-roadmap">
							{t.future.roadmap.map((item, i) => {
								const Icon = ROADMAP_ICONS[i] || Sparkles;
								return (
									<li className="sf-ab-roadmap-item" key={item.title} data-reveal>
										<div className="sf-ab-roadmap-icon">
											<Icon size={18} strokeWidth={1.9} />
										</div>
										<div className="sf-ab-roadmap-content">
											<strong>{item.title}</strong>
											<p>{item.desc}</p>
										</div>
									</li>
								);
							})}
						</ul>
					</div>
				</section>

				{/* JOIN US */}
				<section className="sf-ab-final" data-reveal>
					<div className="sf-ab-final-copy">
						<span className="sf-ab-eyebrow sf-ab-eyebrow-dark">{t.eyebrows.join}</span>
						<h2>{t.joinUs.title}</h2>
						<p>{t.joinUs.text}</p>
					</div>

					{/* One decisive action to close on — the hero already offered both paths. */}
					<div className="sf-ab-final-actions">
						<button className="sf-ab-btn-primary" onClick={() => navigate("/businesslanding")}>
							{t.hero.primaryCta}
							<ArrowRight size={17} className="sf-ab-btn-arrow" />
						</button>
					</div>
				</section>

			</div>
		</div>
	);
};

export default About;
